import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { Box, Collapse, Grid, Modal } from "@mui/material";
import { useSnackbar } from "notistack";
import { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import ViewAndFilterButton from "../components/ViewAndFilterButton";
import ViewOption from "../components/ViewOption";
import FilterWrapper from "../components/general/FiltersWrapper";
import Loader from "../components/general/Loader";
import LoadingBackdrop from "../components/general/LoadingBackdrop";
import NoData from "../components/general/NoData";
import NunitoText from "../components/general/NunitoText";
import ReusableButton from "../components/general/ReusableButton";
import ReusableTextField from "../components/general/ReusableTextField";
import DeleteModal from "../components/modal/DeleteModal";
import RejectModal from "../components/modal/RejectModal";
import AddSchoolModal from "../components/School/AddSchoolModal";
import EditSchoolModal from "../components/School/EditSchoolModal";
import ApproveSchoolModal from "../components/School/ApproveSchoolModal";
import RestoreSchoolModal from "../components/School/RestoreSchoolModal";
import AddUserModal from "../components/User/AddUserModal";
import AddParticipantModal from "../components/Participant/AddParticipantModal";
import HeavyTable from "../components/table/HeavyTable";
import { showWarningSwal } from "../functions/alert";
import {
  isAdmin,
  isAdminOrPartner,
  isAdminOrPartnerOrAssistant,
  isPartnerOrAssistant,
} from "../functions/checkrole";
import { deleteSchool } from "../functions/deleteData";
import {
  buildQueryString,
  cancelAction,
  changeViewOptions,
  checkIfAllSelectedRowsMatchStatus,
  checkIfAnySelectedRowIs,
  controlFilter,
  controlView,
  makeSureIsArray,
} from "../functions/general";
import { enhancedExportSchools, enhancedGetSchools } from "../functions/getData";
import { approveSchool, rejectSchool, restoreSchool } from "../functions/patchData";
import { showNotification } from "../functions/snackbar";

const schoolHeader = () =>
  [
    { id: "name", label: "School/Tuition" },
    { id: "name_in_certificate", label: "name in certificate" },
    isAdmin() && { id: "country_name", label: "Country" },
    { id: "participants_count", label: "participants" },
    { id: "status", label: "Status" },
    { id: "email", label: "Email" },
    isAdminOrPartner() && { id: "teachers", label: "Teachers" },
    { id: "private", label: "Tuition Centre" },
    { id: "address", label: "Address" },
    { id: "postal", label: "Postal Code" },
    { id: "province", label: "Province" },
    { id: "phone", label: "Phone Number" },
    { id: "created_by_username", label: "Submit By" },
    { id: "approved_by_username", label: "Approved By" },
    { id: "rejected_by_username", label: "Rejected By" },
    { id: "modified_by_username", label: "Last Modified By" },
    { id: "reject_reason", label: "Has been rejected" },
  ].filter(Boolean);

export default function EnhancedSchools() {
  document.title = "School";
  const isMounted = useRef(false);
  const user = useSelector((state) => state.user);
  const [header, setHeader] = useState(schoolHeader());
  const [schoolTable, setSchoolTable] = useState();
  const [viewOptions, setViewOptions] = useState([
    { label: "Address", state: true, key: "address" },
    { label: "Postal Code", state: true, key: "postal" },
    { label: "Province", state: true, key: "province" },
    { label: "Phone Number", state: true, key: "phone" },
    { label: "Submit By", state: true, key: "created_by_username" },
    { label: "Approve By", state: true, key: "approved_by_username" },
    { label: "Rejected By", state: true, key: "rejected_by_username" },
    { label: "Last Modified By", state: true, key: "modified_by_username" },
    { label: "Has been rejected", state: true, key: "reject_reason" },
  ]);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);
  const [loadingTable, setLoadingTable] = useState(false);
  const [viewing, setViewing] = useState(false);
  const [filtering, setFiltering] = useState(false);
  const [id, setId] = useState("");
  const [selected, setSelected] = useState([]);
  const [selecting, setSelecting] = useState([]);
  const [onDelete, setOnDelete] = useState(false);
  const [onAddSchool, setOnAddSchool] = useState(false);
  const [onEditSchool, setOnEditSchool] = useState(false);
  const [onApprove, setOnApprove] = useState(false);
  const [onReject, setOnReject] = useState(false);
  const [onRestore, setOnRestore] = useState(false);
  const [onAddUser, setOnAddUser] = useState(false);
  const [onAddParticipant, setOnAddParticipant] = useState(false);
  const [sortAbleColumns, setSortableColumns] = useState([]);

  // Consolidated query parameters state
  const [queryParams, setQueryParams] = useState({
    limits: 10,
    page: 1,
    search: "",
    private: "",
    country: "",
    status: "",
    orderBy: "",
    orderDirection: "",
  });

  const handleView = () => controlView(viewing, setViewing, setFiltering);
  const handleFilter = () => controlFilter(filtering, setFiltering, setViewing);
  const cancelDelete = () => cancelAction(setOnDelete, setId);
  const cancelEdit = () => cancelAction(setOnEditSchool, setId);
  const cancelApprove = () => cancelAction(setOnApprove, setId);
  const cancelReject = () => cancelAction(setOnReject, setId);
  const cancelRestore = () => cancelAction(setOnRestore, setId);
  const cancelAddUser = () => cancelAction(setOnAddUser, setId);
  const cancelAddParticipant = () => cancelAction(setOnAddParticipant, setId);
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();

  const updateQueryParams = (updates) => {
    setQueryParams((prev) => ({
      ...prev,
      ...updates,
    }));
  };

  const resetQueryParams = () => {
    setQueryParams({
      limits: 10,
      page: 1,
      search: "",
      private: "",
      country: "",
      status: "",
      orderBy: "",
      orderDirection: "",
    });
  };

  // Flatten nested objects into the flat keys the table headers expect, while
  // keeping the original fields (country_id, address, postal, etc.) that the
  // shared school modals read directly from table.data.
  const formatTableData = (data) =>
    makeSureIsArray(data).map((el) => ({
      ...el,
      country_name: el.country?.display_name,
      created_by_username: el.created_by?.username,
      approved_by_username: el.approved_by?.username,
      rejected_by_username: el.rejected_by?.username,
      modified_by_username: el.modified_by?.username,
    }));

  const fetchData = () => {
    if (!controller.current) return;
    setSelected([]);
    setSelecting([]);
    setLoading(true);

    const queryString = buildQueryString(queryParams);
    enhancedGetSchools(queryString, controller.current.signal).then((res) => {
      res.data = formatTableData(res.data);
      setSchoolTable(res);
      setSortableColumns(res?.sortable_columns);
      setLoading(false);
    });
  };

  const afterFunction = (cancel, data) => {
    if ([200, 201].includes(data.status)) {
      resetQueryParams();
      setSearch("");
      fetchData();
      cancel();
      showNotification("success", data.message, enqueueSnackbar, closeSnackbar);
      setSelected([]);
      setSelecting([]);
    }
  };

  const deleteFunction = () => {
    setLoading(true);
    deleteSchool({ id: id === "" ? selected : [id] })
      .then((d) => {
        afterFunction(cancelDelete, d);
      })
      .catch((e) => console.log(e))
      .finally(() => isMounted.current && setLoading(false));
  };
  const approveFunction = () => {
    setLoading(true);
    approveSchool({ id: id === "" ? selected : [id] })
      .then((d) => {
        afterFunction(cancelApprove, d);
      })
      .catch((e) => console.log(e))
      .finally(() => isMounted.current && setLoading(false));
  };
  const rejectFunction = (reason) => {
    setLoading(true);
    rejectSchool({ id: [id], reject_reason: [reason] })
      .then((d) => {
        afterFunction(cancelReject, d);
      })
      .catch((e) => console.log(e))
      .finally(() => isMounted.current && setLoading(false));
  };
  const restoreFunction = () => {
    setLoading(true);
    restoreSchool({ id: [id] })
      .then((d) => {
        afterFunction(cancelRestore, d);
      })
      .catch((e) => console.log(e))
      .finally(() => isMounted.current && setLoading(false));
  };

  const adjustEndpoint = (endpoint, value) => {
    const currentParams = { ...queryParams };

    // Remove the current filter value if it's being changed
    if (value !== currentParams.private && currentParams.private) {
      endpoint += `&private=${currentParams.private}`;
    }
    if (value !== currentParams.country && currentParams.country) {
      endpoint += `&country=${currentParams.country}`;
    }
    if (value !== currentParams.status && currentParams.status) {
      endpoint += `&status=${currentParams.status}`;
    }

    return endpoint;
  };

  const url = "school/list?get_filter=";
  const filters = [
    {
      label: "School/Tuition",
      endpoint: `${url}private`,
      value: queryParams.private,
      onChange: (value) => updateQueryParams({ private: value }),
      key: "private",
      setValue: (value) => updateQueryParams({ private: value }),
    },
    {
      label: "country",
      endpoint: `${url}country`,
      value: queryParams.country,
      onChange: (value) => updateQueryParams({ country: value }),
      key: "country",
      setValue: (value) => updateQueryParams({ country: value }),
    },
    {
      label: "Status",
      endpoint: `${url}status`,
      value: queryParams.status,
      onChange: (value) => updateQueryParams({ status: value }),
      key: "status",
      setValue: (value) => updateQueryParams({ status: value }),
    },
  ];

  const onChangeViewOptions = (value) =>
    changeViewOptions(schoolHeader(), setViewOptions, setHeader, value);

  const handleSortingFunction = (sortingKey, direction) => {
    updateQueryParams({
      orderBy: sortingKey,
      orderDirection: direction,
    });
  };

  const onChangeSearch = (value) => {
    setSearch(value);
    updateQueryParams({ search: value });
  };

  const onChangePage = (pageNo) => {
    updateQueryParams({ page: pageNo });
  };

  const onChangeRowsPerPage = (numberOfRows) => {
    setRowsPerPage(numberOfRows);
    updateQueryParams({ limits: numberOfRows });
  };

  const controller = useRef(null);
  const isFirstRender = useRef(true);

  // Effect for initial data fetch
  useEffect(() => {
    if (isFirstRender.current) isFirstRender.current = false;
    isMounted.current = true;

    controller.current = new AbortController();

    setSelected([]);
    setSelecting([]);
    setLoading(true);

    const queryString = buildQueryString(queryParams);
    enhancedGetSchools(queryString, controller.current.signal).then((res) => {
      res.data = formatTableData(res.data);
      setSchoolTable(res);
      setSortableColumns(res?.sortable_columns);
      setLoading(false);
    });

    return () => {
      if (controller.current) {
        controller.current.abort();
      }
      isMounted.current = false;
    };
  }, []);

  // Effect for data fetching when query parameters change
  useEffect(() => {
    if (controller.current && !isFirstRender.current) {
      setSelected([]);
      setSelecting([]);
      setLoading(true);

      const queryString = buildQueryString(queryParams);
      enhancedGetSchools(queryString, controller.current.signal).then((res) => {
        res.data = formatTableData(res.data);
        setSchoolTable(res);
        setSortableColumns(res?.sortable_columns);
        setLoading(false);
      });
    }
  }, [queryParams]);

  const onClickMassApprove = () => {
    return schoolTable &&
      checkIfAllSelectedRowsMatchStatus(
        selected,
        schoolTable.data,
        ["pending"],
        user
      )
      ? setOnApprove(true)
      : showWarningSwal(
          "Please only select pending schools that are not added by you to mass approve"
        );
  };
  const onClickMassDelete = () => {
    if (!selected.length)
      return showWarningSwal("Please select at least one school");
    if (isAdmin()) {
      return schoolTable &&
        checkIfAnySelectedRowIs(selected, schoolTable.data, "deleted")
        ? showWarningSwal("Please select schools that aren't already deleted to delete")
        : setOnDelete(true);
    } else if (isPartnerOrAssistant()) {
      return schoolTable &&
        checkIfAllSelectedRowsMatchStatus(selected, schoolTable.data, [
          "pending",
          "rejected",
        ])
        ? setOnDelete(true)
        : showWarningSwal("You can only delete pending or rejected schools");
    }
  };

  const exportData = () => {
    setLoading(true);
    // Export the full filtered set (filters + search), without pagination params.
    // The endpoint returns a ready CSV file, which is downloaded directly.
    const exportParams = buildQueryString({
      search: queryParams.search,
      private: queryParams.private,
      country: queryParams.country,
      status: queryParams.status,
    });
    enhancedExportSchools(exportParams)
      .catch((err) => console.log(err))
      .finally(() => isMounted.current && setLoading(false));
  };

  return (
    <Box className="wrapperBox">
      <LoadingBackdrop loading={loading} />
      <Collapse in={viewing}>
        <ViewOption
          viewOptions={viewOptions}
          rowsPerPage={rowsPerPage}
          onChangeRowsPerPage={onChangeRowsPerPage}
          onChangeViewOptions={onChangeViewOptions}
        />
      </Collapse>
      <Collapse in={filtering}>
        <FilterWrapper filters={filters} adjustEndpoint={adjustEndpoint} />
      </Collapse>
      <Grid
        className="firstRowContainer"
        container
        justifyContent="space-between"
      >
        <div className="dashboardAndSelfBtnDiv">
          <ReusableButton
            text="Dashboard"
            fontSize={14}
            bgColor="#F16774"
            height={36}
            width={125}
            br={18}
            to="/dashboard"
            iconType="home"
          />
          <ChevronRightIcon />
          <ReusableButton
            text="Schools"
            fontSize={14}
            bgColor="#F16774"
            height={36}
            br={18}
          />
        </div>
        <div className="viewAndFilterBtnDiv">
          <ViewAndFilterButton
            text="View Options"
            state={viewing}
            fontSize={14}
            height={42}
            onClick={handleView}
            marginRight={10}
          />
          <ViewAndFilterButton
            text="Filter"
            state={filtering}
            fontSize={14}
            height={42}
            onClick={handleFilter}
          />
        </div>
      </Grid>
      <Grid className="tableContainer" container>
        <NunitoText
          value="Schools"
          fontSize={40}
          fontWeight={700}
          italic
          color="#144A94"
        />
        <Grid
          className="searchAndBtnContainer"
          container
          alignItems="center"
          justifyContent="space-between"
        >
          <ReusableTextField
            type="search"
            width={500}
            height={60}
            bgColor="#F2F2F2"
            placeholder="Search using keyword"
            state={search}
            setState={onChangeSearch}
            onBlur
          />
          <div>
            {isAdminOrPartnerOrAssistant() && (
              <ReusableButton
                text="Export CSV"
                fontSize={15}
                bgColor="#5E75C3"
                height={46}
                marginRight={20}
                iconType="export"
                onClick={() => exportData()}
              />
            )}
            {isAdminOrPartnerOrAssistant() && (
              <ReusableButton
                text="Import CSV"
                fontSize={15}
                bgColor="#5E75C3"
                height={46}
                marginRight={20}
                iconType="import"
                to="csvupload"
              />
            )}
          </div>
          {isAdminOrPartnerOrAssistant() && (
            <div className="endBtnDiv">
              <ReusableButton
                text="Add School"
                fontSize={15}
                bgColor="#5E75C3"
                height={46}
                marginRight={20}
                onClick={() => setOnAddSchool(true)}
                iconType="add"
              />
              {isAdminOrPartner() && (
                <ReusableButton
                  text="Mass Approve"
                  fontSize={15}
                  bgColor="#5E75C3"
                  height={46}
                  marginRight={20}
                  onClick={() => onClickMassApprove()}
                  iconType="check"
                />
              )}
              <ReusableButton
                text="Mass Delete"
                fontSize={15}
                bgColor="#E83042"
                height={46}
                marginRight={20}
                onClick={() => onClickMassDelete()}
                iconType="delete"
              />
            </div>
          )}
        </Grid>
        <NunitoText
          value='Search only in "School/Tuition", "Address", "Postal Code", "Province" and "Phone Number" column'
          fontSize={20}
          fontWeight={400}
          italic
          color="#144A94"
        />
        {loadingTable && <Loader height={600} />}
        {!loadingTable &&
          schoolTable &&
          makeSureIsArray(schoolTable.data).length && (
            <HeavyTable
              handleSortingFunction={handleSortingFunction}
              sortableKeys={sortAbleColumns}
              headers={header}
              list={schoolTable}
              title="schools"
              rowsPerPage={rowsPerPage}
              setOnDelete={setOnDelete}
              onChangePage={onChangePage}
              fixed={["name", "country_name"]}
              selecting={selecting}
              setSelecting={setSelecting}
              selected={selected}
              setSelected={setSelected}
              setId={setId}
              setOnApprove={setOnApprove}
              setOnReject={setOnReject}
              setOnRestore={setOnRestore}
              setOnEdit={setOnEditSchool}
              setOnAddUser={setOnAddUser}
              setOnAddParticipant={setOnAddParticipant}
            />
          )}
        {!loadingTable &&
          schoolTable &&
          !makeSureIsArray(schoolTable.data).length && <NoData height={600} />}
      </Grid>
      <Modal open={onDelete} onClose={() => cancelDelete()}>
        <>
          {schoolTable && (
            <DeleteModal
              table={schoolTable}
              id={id}
              selected={selected}
              deleteFunction={deleteFunction}
              cancelDelete={cancelDelete}
            />
          )}
        </>
      </Modal>
      <Modal open={onApprove} onClose={() => cancelApprove()}>
        <>
          {schoolTable && (
            <ApproveSchoolModal
              table={schoolTable}
              id={id}
              selected={selected}
              approveFunction={approveFunction}
              cancelApprove={cancelApprove}
              object="schools"
            />
          )}
        </>
      </Modal>
      <Modal open={onReject} onClose={() => cancelReject()}>
        <>
          {schoolTable && (
            <RejectModal
              table={schoolTable}
              id={id}
              rejectFunction={rejectFunction}
              cancelReject={cancelReject}
            />
          )}
        </>
      </Modal>
      <Modal open={onRestore} onClose={() => cancelRestore()}>
        <>
          {schoolTable && (
            <RestoreSchoolModal
              table={schoolTable}
              id={id}
              restoreFunction={restoreFunction}
              cancelRestore={cancelRestore}
              object="schools"
            />
          )}
        </>
      </Modal>
      <Modal open={onAddSchool} onClose={() => setOnAddSchool(false)}>
        <>
          <AddSchoolModal
            setLoading={setLoading}
            setOnAddSchool={setOnAddSchool}
            afterAddingSchool={afterFunction}
          />
        </>
      </Modal>
      <Modal open={onEditSchool} onClose={() => cancelEdit()}>
        <>
          {schoolTable && (
            <EditSchoolModal
              setLoading={setLoading}
              table={schoolTable}
              id={id}
              afterEditingSchool={afterFunction}
              cancelEdit={cancelEdit}
            />
          )}
        </>
      </Modal>
      <Modal open={onAddUser} onClose={() => cancelAddUser()}>
        <>
          {schoolTable && (
            <AddUserModal
              setLoading={setLoading}
              setOnAddUser={setOnAddUser}
              table={schoolTable}
              id={id}
              afterAddingUser={afterFunction}
              cancelAddUser={cancelAddUser}
            />
          )}
        </>
      </Modal>
      <Modal open={onAddParticipant} onClose={() => cancelAddParticipant()}>
        <>
          {schoolTable && (
            <AddParticipantModal
              setLoading={setLoading}
              setOnAddParticipant={setOnAddParticipant}
              table={schoolTable}
              id={id}
              afterAddingParticipant={afterFunction}
              cancelAddParticipant={cancelAddParticipant}
            />
          )}
        </>
      </Modal>
    </Box>
  );
}
